import { useState } from "react";
import { syncApiUrl } from "@cliqsuite/db";
import { useAuth, type OwnerUser } from "../../lib/auth";
import { AuthFrame, AuthHead, IconField } from "./AuthFrame";

/**
 * Shared by AcceptInvitePage (a teammate setting their own password for the
 * first time, from the invite email that replaced the old owner-types-it-
 * directly flow) and ResetPasswordPage (forgot-password recovery) — same
 * shape either way: a token from the emailed link + a new password, POSTed
 * to whichever endpoint, logging the person straight in on success (both
 * /auth/accept-invite and /auth/reset-password return a fresh
 * { user, token } just like login/signup).
 */
export function SetNewPasswordPage({
  kind,
  token,
  onDone,
}: {
  kind: "invite" | "reset";
  token: string;
  onDone: () => void;
}) {
  const { setSession } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const path = kind === "invite" ? "/auth/accept-invite" : "/auth/reset-password";
  const heading = kind === "invite" ? "Set your password" : "Choose a new password";
  const subheading =
    kind === "invite" ? "You've been invited to CliqSuite. Set a password to get started." : "Use at least 8 characters.";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(syncApiUrl(path), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        setError("This link is invalid or has expired. Ask for a new one and try again.");
        setLoading(false);
        return;
      }
      const data = (await res.json()) as { ok: boolean; user: OwnerUser; token: string };
      setSession(data.user, data.token);
      onDone();
    } catch {
      setError("Couldn't reach the server. Check your connection and try again.");
      setLoading(false);
    }
  };

  return (
    <AuthFrame>
      <form className="o-auth__form" onSubmit={submit}>
        <AuthHead icon={kind === "invite" ? "key" : "lock_reset"} title={heading} sub={subheading} />
        <IconField label="New password" icon="lock" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
        <IconField label="Confirm password" icon="lock" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        {error && (
          <div className="o-error" role="alert" style={{ marginTop: 0 }}>
            {error}
          </div>
        )}
        <button type="submit" className="o-auth__submit" disabled={loading}>
          {loading ? "Saving…" : kind === "invite" ? "Set password and continue" : "Reset password and continue"}
          {!loading && <span className="ms" aria-hidden="true">arrow_forward</span>}
        </button>
      </form>
    </AuthFrame>
  );
}
